import { useEffect, useCallback } from 'react';

/**
 * Custom hook for handling keyboard shortcuts
 * @param {Object} keyMap - Map of key combinations to handlers
 * @param {boolean} enabled - Whether the hotkeys are enabled
 */
export function useHotkeys(keyMap, enabled = true) {
  const handleKeyDown = useCallback((event) => {
    if (!enabled || !keyMap || !event.key) return;
    
    // Don't trigger when typing in inputs
    const target = event.target;
    const isInput = target.tagName === 'INPUT' || 
                    target.tagName === 'TEXTAREA' || 
                    target.isContentEditable;
    
    Object.entries(keyMap).forEach(([key, handler]) => {
      if (!key || typeof key !== 'string') return;
      
      const keys = key.toLowerCase().split('+');
      const hasCtrl = keys.includes('ctrl') || keys.includes('cmd');
      const hasShift = keys.includes('shift');
      const hasAlt = keys.includes('alt');
      let mainKey = keys.find(k => !['ctrl', 'cmd', 'shift', 'alt'].includes(k));
      
      if (!mainKey) return;
      
      // Handle special character keys
      const specialKey = mainKey === '?' && event.key === '?';
      
      const ctrlMatch = hasCtrl ? (event.ctrlKey || event.metaKey) : (!event.ctrlKey && !event.metaKey);
      const shiftMatch = hasShift ? event.shiftKey : (specialKey ? true : !event.shiftKey);
      const altMatch = hasAlt ? event.altKey : !event.altKey;
      const keyMatch = specialKey || (event.key && event.key.toLowerCase() === mainKey);
      
      // For special keys that should work even in inputs
      const isGlobalShortcut = hasCtrl || hasAlt;
      
      if (ctrlMatch && shiftMatch && altMatch && keyMatch) {
        if (!isInput || isGlobalShortcut) {
          event.preventDefault();
          handler(event);
        }
      }
    });
  }, [keyMap, enabled]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Hook to register a single hotkey
 * @param {string} key - Key combination (e.g., 'ctrl+k')
 * @param {Function} callback - Handler function
 * @param {boolean} enabled - Whether the hotkey is enabled
 */
export function useHotkey(key, callback, enabled = true) {
  useHotkeys({ [key]: callback }, enabled);
}
